/**
 * utils.js, Helper to extract clusters and reservations arrays from a user object.
 * Returns { clusters, reservations }.
 */
export const extractClustersAndReservations = (userObj) => {
  const clusters = Array.isArray(userObj.clusters) ? userObj.clusters : [];
  const reservations = Array.isArray(userObj.reservations) ? userObj.reservations : [];
  return { clusters, reservations };
}

/**
 * Normalizes a reservation object to a standard shape.
 * Handles location parsing and details default.
 *
 * @param {Object} r - The reservation object.
 * @returns {Object} Normalized reservation object.
 */
export const normalizeReservation = (r) => {
  if (!r) return null;
  let locationObj = null;
  if (r.location && typeof r.location === 'string') {
    const [lat, lng] = r.location.split(',').map(coord => parseFloat(coord.trim()));
    if (!isNaN(lat) && !isNaN(lng)) {
      locationObj = { lat, lng };
    }
  } else if (r.location && typeof r.location === 'object') {
    locationObj = r.location;
  }
  return {
    id: r.id,
    name: r.name,
    details: r.details || '',
    location: locationObj,
    isProGroup:r.is_prod_group
  };
}

/**
 * Groups reservations by cluster name (including 'none'), ignoring duplicate cluster names.
 *
 * @param {Object} userObject - The input object with clusters and/or reservations arrays.
 * @returns {Object} An object with cluster names as keys and arrays of reservation objects as values.
 *
 * Input format:
 * {
 *   clusters: [{ name: string, reservations: [{ id, name, ... }, ...] }, ...],
 *   reservations: [{ id, name, ... }, ...]
 * }
 *
 * Output format:
 * {
 *   [clusterName]: [reservationObj, ...],
 *   none: [reservationObj, ...]
 * }
 *
 * If the input format changes, update the mapping logic to normalize each reservation object.
 */
export const transformReservations = (userObject) => {
  const { clusters, reservations } = extractClustersAndReservations(userObject);
  if (clusters.length === 0 && reservations.length === 0) return {};
  const result = {};
  const seenClusters = new Set();

  clusters.forEach(clusterObj => {
    const clusterName = clusterObj.name;
    if (!clusterName || seenClusters.has(clusterName)) return;
    seenClusters.add(clusterName);
    const reservationsArr = Array.isArray(clusterObj.reservations) ? clusterObj.reservations : [];
    result[clusterName] = reservationsArr.map(r => ({ ...normalizeReservation(r), cluster: clusterName }));
  });

  result['none'] = reservations.map(r => ({ ...normalizeReservation(r), cluster: 'none' }));

  if (clusters.length === 0 && !result['none']) {
    result['none'] = [];
  }

  return result;
};

/**
 * Returns a unique list of reservation objects from both clusters and top-level reservations.
 *
 * @param {Object} userObject - The input object with clusters and/or reservations arrays.
 * @returns {Array} Array of unique reservation objects (by id).
 *
 * Input format:
 * {
 *   clusters: [{ name: string, reservations: [{ id, name, ... }, ...] }, ...],
 *   reservations: [{ id, name, ... }, ...]
 * }
 *
 * Output format:
 * [
 *   { id: string, name: string, location?: string|object, details?: string, ... },
 *   ...
 * ]
 *
 * Notes:
 * - If the input format changes (e.g., reservations are strings, missing ids, or new properties),
 *   update the logic to normalize each reservation object to the expected output shape.
 * - Reservations are considered unique by their 'id' property.
 * - If reservations are strings, convert them to objects with generated ids and names.
 * - Always ensure each reservation in the output has at least: { id, name, details, location }
 */
export const getUniqueReservations = (userObject) => {
  const { clusters, reservations } = extractClustersAndReservations(userObject);
  if (clusters.length === 0 && reservations.length === 0) return [];
  const uniqueMap = new Map();

  clusters.forEach(clusterObj => {
    const reservationsArr = Array.isArray(clusterObj.reservations) ? clusterObj.reservations : [];
    reservationsArr.forEach(r => {
      if (r && r.id && !uniqueMap.has(r.id)) {
        uniqueMap.set(r.id, normalizeReservation(r));
      }
    });
  });

  reservations.forEach(r => {
    if (r && r.id && !uniqueMap.has(r.id)) {
      uniqueMap.set(r.id, normalizeReservation(r));
    }
  });

  return Array.from(uniqueMap.values());
};

/**
 * the following two functions lookupReservationsByIds and getRecents are used just for testing purpose 
 * it will be used based on the client requirement.
 */

/**
 * Looks up reservations by a provided array of ids (cluster or reservation ids), returning a flat array of reservation objects.
 *
 * @param {Object} userObj - The user object containing clusters and reservations.
 * @param {Array} ids - A flat array of cluster or reservation ids.
 * @returns {Array} A flat array of reservation objects, each included only once.
 *
 * Input format:
 * {
 *   clusters: [{ id, name, reservations: [...] }, ...],
 *   reservations: [{ id, name, ... }, ...],
 *   ...
 * }
 * ids: [ 'id1', 'id2', ... ] // Each id is a cluster or reservation id
 *
 * Output format:
 * [
 *   { id, name, details, location, ... },
 *   ...
 * ]
 *
 * Notes:
 * - If an id is a cluster id, all reservations in that cluster are included in the result.
 * - If an id is a reservation id, that reservation is included in the result.
 * - Each reservation appears only once in the result.
 * - If ids is not provided or not an array, returns an empty array.
 */
export const lookupReservationsByIds = (userObj = {}, ids) => {
  if (!ids || !Array.isArray(ids) || ids.length === 0) return [];
  const { clusters, reservations } = extractClustersAndReservations(userObj);
  if (clusters.length === 0 && reservations.length === 0) return [];

  // Build a map for quick reservation lookup
  const reservationMap = new Map();
  reservations.forEach(r => {
    if (r && r.id) reservationMap.set(r.id, normalizeReservation(r));
  });
  clusters.forEach(cluster => {
    if (Array.isArray(cluster.reservations)) {
      cluster.reservations.forEach(r => {
        if (r && r.id && !reservationMap.has(r.id)) reservationMap.set(r.id, normalizeReservation(r));
      });
    }
  });

  const result = [];
  ids.forEach(id => {
    const cluster = clusters.find(c => c.id === id);
    if (cluster && Array.isArray(cluster.reservations)) {
      cluster.reservations.forEach(r => {
        const norm = normalizeReservation(r);
        if (norm && !result.some(x => x.id === norm.id)) result.push(norm);
      });
    } else {
      const reservation = reservationMap.get(id);
      if (reservation && !result.some(x => x.id === reservation.id)) result.push(reservation);
    }
  });

  return result;
};

/**
 * Find the names of clusters or reservations for each group of recents in userObject.
 *
 * @param {Object} userObj - The user object containing clusters, reservations, and recents.
 * @returns {Array} An array of objects, each with name and ids properties.
 *
 * Input format:
 * {
 *   clusters: [{ id, name, reservations: [...] }, ...],
 *   reservations: [{ id, name, ... }, ...],
 *   recents: [[id1,id2,id3],[id4,id5,id6], ...] // Each id is a cluster or reservation id
 * }
 *
 * Output format:
 * [ { name: <clusterName && reservationName && ...>, ids: [id1, id2, id3] },
 *   { name: <clusterName && reservationName && ...>, ids: [id4, id5, id6] },
 *   ...
 * ]
 *
 * Notes:
 * - If an id is a cluster id, the name is the cluster name.
 * - If an id is a reservation id, the name is the reservation name.
 * - If recents is not provided or not an array, returns an empty array.
 * 
**/
export const getRecents = (userObj = {}) => {
  const recents = Array.isArray(userObj.recents) ? userObj.recents : [];
  if (recents.length === 0) return [];
  const { clusters, reservations } = extractClustersAndReservations(userObj);

  // Build lookup maps
  const clusterMap = new Map();
  clusters.forEach(c => { if (c && c.id) clusterMap.set(c.id, c); });
  const reservationMap = new Map();
  reservations.forEach(r => { if (r && r.id) reservationMap.set(r.id, r); });

  const result = [];
  recents.forEach(group => {
    if (!Array.isArray(group) || group.length === 0) return;
    // Collect all names for the group
    const names = group.map(id => {
      if (clusterMap.has(id)) {
        return clusterMap.get(id).name;
      } else if (reservationMap.has(id)) {
        return reservationMap.get(id).name;
      } else {
        // Try to find in cluster reservations
        for (const cluster of clusters) {
          if (Array.isArray(cluster.reservations)) {
            const found = cluster.reservations.find(r => r.id === id);
            if (found) {
              return found.name;
            }
          }
        }
      }
      return '';
    }).filter(Boolean);
    const name = names.join(' && ');
    result.push({name, ids: group });
  });
  return result;
};
